import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../supabaseClient';
import LanguageToggle from '../../components/LanguageToggle';
import VocationWorksLogo from '../../components/VocationWorksLogo';

const NAVY     = '#0D1B2A';
const NAVY_MID = '#132233';
const AMBER    = '#F5A623';
const WHITE    = '#FFFFFF';
const FONT_HEAD = "'Bebas Neue', sans-serif";
const FONT_BODY = "'DM Sans', sans-serif";

export default function AdminLogin() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [email, setEmail]       = useState('');
  const [password, setPassword] = useState('');
  const [error, setError]       = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const link = document.createElement('link');
    link.rel  = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Bebas+Neue&family=DM+Sans:wght@400;500;600;700&display=swap';
    document.head.appendChild(link);
    const style = document.createElement('style');
    style.textContent = `*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; } body { background: ${NAVY}; } input::placeholder { color: rgba(255,255,255,0.28); }`;
    document.head.appendChild(style);
    return () => { document.head.removeChild(link); document.head.removeChild(style); };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    const { error: signInError } = await supabase.auth.signInWithPassword({ email, password });
    if (signInError) {
      setError(signInError.message);
      setPassword('');
      setSubmitting(false);
      return;
    }

    const { data: isAdmin, error: isAdminError } = await supabase.rpc('is_admin');
    if (isAdminError || !isAdmin) {
      await supabase.auth.signOut();
      setError(t('admin.login.noAdminAccessError'));
      setPassword('');
      setSubmitting(false);
      return;
    }

    navigate('/admin/dashboard');
  };

  const handleForgotPassword = async () => {
    if (!email) { alert(t('auth.login.enterEmailFirst')); return; }
    const { error: resetError } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: window.location.origin + '/reset-password',
    });
    alert(resetError ? resetError.message : t('auth.login.resetEmailSent'));
  };

  return (
    <div style={{ background: NAVY, minHeight: '100dvh', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: FONT_BODY, padding: '20px' }}>
      <div style={{ position: 'fixed', top: '16px', right: '16px' }}>
        <LanguageToggle />
      </div>
      <div style={{ background: NAVY_MID, borderRadius: '16px', padding: '2.5rem 2rem', width: '100%', maxWidth: '400px', boxShadow: '0 4px 40px rgba(0,0,0,0.4)' }}>

        {/* Logo */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', marginBottom: '1.75rem' }}>
          <VocationWorksLogo variant="reversed" size={36} />
        </div>

        <div style={{ textAlign: 'center', marginBottom: '1.75rem' }}>
          <span style={{ fontSize: '0.68rem', fontWeight: 700, color: AMBER, letterSpacing: '0.14em', textTransform: 'uppercase' }}>
            {t('admin.login.accessLabel')}
          </span>
        </div>

        <form onSubmit={handleSubmit}>
          <label style={{ display: 'block', fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)', marginBottom: '6px' }}>
            {t('admin.login.emailLabel')}
          </label>
          <input
            type="email"
            value={email}
            onChange={e => { setEmail(e.target.value); setError(''); }}
            placeholder="you@vocationworks.io"
            autoFocus
            style={{
              width: '100%', padding: '11px 13px', fontSize: '0.95rem',
              border: '1.5px solid rgba(255,255,255,0.12)',
              borderRadius: '8px', color: WHITE, outline: 'none',
              backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY,
              marginBottom: '1.1rem',
            }}
          />

          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '6px' }}>
            <label style={{ fontSize: '0.82rem', fontWeight: 600, color: 'rgba(255,255,255,0.55)' }}>
              {t('admin.login.passwordLabel')}
            </label>
            <span
              onClick={handleForgotPassword}
              style={{ fontSize: '0.8125rem', color: AMBER, fontWeight: 500, cursor: 'pointer' }}
            >
              {t('auth.login.forgotPassword')}
            </span>
          </div>
          <input
            type="password"
            value={password}
            onChange={e => { setPassword(e.target.value); setError(''); }}
            placeholder={t('auth.login.passwordPlaceholder')}
            style={{
              width: '100%', padding: '11px 13px', fontSize: '0.95rem',
              border: `1.5px solid ${error ? '#F87171' : 'rgba(255,255,255,0.12)'}`,
              borderRadius: '8px', color: WHITE, outline: 'none',
              backgroundColor: 'rgba(255,255,255,0.06)', fontFamily: FONT_BODY,
              marginBottom: error ? '0.6rem' : '1.25rem',
            }}
          />
          {error && (
            <p style={{ color: '#F87171', fontSize: '0.84rem', fontWeight: 500, marginBottom: '1.25rem' }}>
              {error}
            </p>
          )}
          <button
            type="submit"
            disabled={submitting}
            style={{
              width: '100%', background: AMBER, border: 'none', color: NAVY,
              fontWeight: 700, fontSize: '0.95rem', padding: '12px',
              borderRadius: '8px', cursor: submitting ? 'default' : 'pointer', fontFamily: FONT_BODY,
              opacity: submitting ? 0.7 : 1,
            }}
          >
            {submitting ? t('auth.login.signingIn') : t('admin.login.signInButton')}
          </button>
        </form>

        <p style={{ marginTop: '1.5rem', textAlign: 'center', fontSize: '0.75rem', color: 'rgba(255,255,255,0.2)' }}>
          {t('admin.login.footerPrefix')}<Link to="/admin/signup" style={{ color: 'rgba(255,255,255,0.35)' }}>{t('admin.login.createAccountLink')}</Link>
        </p>
      </div>
    </div>
  );
}
